import axios from "axios";
import * as oauth from "oauth4webapi";

import {
  type StoredAuth,
  type UdzsAuthClient,
  type UdzsAuthClientOptions,
} from "./auth-client-types";
import {
  AuthFlowRestartRequiredError,
  AuthLoginRecoveryFailedError,
  AuthUnauthorizedError,
} from "./auth-errors";
import {
  DEFAULT_AUTH_STORAGE_KEY,
  DEFAULT_ME_STORAGE_KEY,
  DEFAULT_REDIRECT_URL_KEY,
  normalizeRedirectPath,
  readStoredAuth,
  toInternalRedirectPath,
  writeStoredAuth,
} from "./auth-storage";

type OAuthTransaction = {
  codeVerifier: string;
  state: string;
};

export function createUdzsAuthClient(
  options: UdzsAuthClientOptions,
): UdzsAuthClient {
  const resolvedOptions = {
    ...options,
    localstorageAuthKey:
      options.localstorageAuthKey ?? DEFAULT_AUTH_STORAGE_KEY,
    localstorageMeKey: options.localstorageMeKey ?? DEFAULT_ME_STORAGE_KEY,
    redirectUrlKey: options.redirectUrlKey ?? DEFAULT_REDIRECT_URL_KEY,
    basename: normalizeBasePath(options.basename),
  };

  let as: oauth.AuthorizationServer | null = null;
  let auth = readStoredAuth(resolvedOptions.localstorageAuthKey);
  let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
  let loginRedirectPromise: Promise<void> | null = null;
  let recoveryLoginUrlPromise: Promise<string> | null = null;
  const tokenListeners = new Set<(token: string | null) => void>();

  const emitToken = (token: string | null) => {
    tokenListeners.forEach((listener) => listener(token));
  };

  const setAuth = (nextAuth: StoredAuth | null) => {
    auth = nextAuth;

    if (nextAuth) {
      writeStoredAuth(resolvedOptions.localstorageAuthKey, nextAuth);
    } else {
      localStorage.removeItem(resolvedOptions.localstorageAuthKey);
    }

    emitToken(nextAuth?.accessToken ?? null);
  };

  const clearAuthorization = () => {
    axios.defaults.headers.common.Authorization = "";
    emitToken(null);
  };

  const clearLocalAuth = () => {
    stopRefreshScheduler();
    setAuth(null);
    clearAuthorization();
    localStorage.removeItem(resolvedOptions.localstorageMeKey);
  };

  const initAs = async () => {
    if (as !== null) {
      return as;
    }

    const issuerUrl = new URL(resolvedOptions.issuer);
    const response = await oauth.discoveryRequest(issuerUrl);
    as = await oauth.processDiscoveryResponse(issuerUrl, response);

    return as;
  };

  const initClient = (): oauth.Client => ({
    client_id: resolvedOptions.clientId,
    client_secret: resolvedOptions.clientSecret,
    token_endpoint_auth_method: resolvedOptions.clientSecret
      ? "client_secret_basic"
      : "none",
  });

  const getPublicPath = (path: string) => `${resolvedOptions.basename}${path}`;

  const redirectUri = () =>
    window.location.origin + getPublicPath(resolvedOptions.redirectPath);
  const logoutRedirectUri = () =>
    window.location.origin + getPublicPath(resolvedOptions.logoutRedirectPath);
  const oauthTransactionKey = `${resolvedOptions.localstorageAuthKey}:oauth-transaction`;
  const oauthRecoveryKey = `${resolvedOptions.localstorageAuthKey}:oauth-recovery`;

  const readOAuthTransaction = (): OAuthTransaction | null => {
    const stored = sessionStorage.getItem(oauthTransactionKey);

    if (!stored) {
      return null;
    }

    try {
      const value: unknown = JSON.parse(stored);

      if (
        typeof value === "object" &&
        value !== null &&
        "codeVerifier" in value &&
        typeof value.codeVerifier === "string" &&
        "state" in value &&
        typeof value.state === "string"
      ) {
        return value as OAuthTransaction;
      }
    } catch {
      // Invalid browser storage is treated as a missing OAuth transaction.
    }

    sessionStorage.removeItem(oauthTransactionKey);

    return null;
  };

  const createLoginUrl = async () => {
    const authorizationServer = await initAs();
    const oauthClient = initClient();
    const codeVerifier = oauth.generateRandomCodeVerifier();
    const codeChallenge = await oauth.calculatePKCECodeChallenge(codeVerifier);
    const state = oauth.generateRandomState();
    const authorizationUrl = new URL(
      authorizationServer.authorization_endpoint!,
    );

    sessionStorage.setItem(
      oauthTransactionKey,
      JSON.stringify({ codeVerifier, state } satisfies OAuthTransaction),
    );
    authorizationUrl.searchParams.set("client_id", oauthClient.client_id);

    if (resolvedOptions.clientSecret) {
      authorizationUrl.searchParams.set(
        "client_secret",
        resolvedOptions.clientSecret,
      );
    }

    authorizationUrl.searchParams.set("code_challenge", codeChallenge);
    authorizationUrl.searchParams.set("code_challenge_method", "S256");
    authorizationUrl.searchParams.set("redirect_uri", redirectUri());
    authorizationUrl.searchParams.set("response_type", "code");
    authorizationUrl.searchParams.set("scope", "openid");
    authorizationUrl.searchParams.set("state", state);

    return authorizationUrl.toString();
  };

  const refreshToken = async () => {
    if (!auth?.refreshToken) {
      clearLocalAuth();
      throw new AuthUnauthorizedError();
    }

    const authorizationServer = await initAs();
    const client = initClient();
    const response = await oauth.refreshTokenGrantRequest(
      authorizationServer,
      client,
      auth.refreshToken,
    );

    const challenges = oauth.parseWwwAuthenticateChallenges(response);
    if (challenges) {
      clearLocalAuth();
      throw new AuthUnauthorizedError();
    }

    const result = await oauth.processRefreshTokenResponse(
      authorizationServer,
      client,
      response,
    );

    if (oauth.isOAuth2Error(result)) {
      clearLocalAuth();
      throw new AuthUnauthorizedError();
    }

    setAuth({
      ...auth,
      accessToken: result.access_token,
      refreshToken: result.refresh_token ?? auth.refreshToken,
      expiresAt:
        result.expires_in !== undefined
          ? Date.now() + result.expires_in * 1000
          : null,
    });

    scheduleRefreshToken();

    return auth?.accessToken ?? null;
  };

  const ensureValidAccessToken = async () => {
    if (!auth) {
      return null;
    }

    if (
      auth.expiresAt !== null &&
      auth.expiresAt - Date.now() <= resolvedOptions.timeReserveMs
    ) {
      return refreshToken();
    }

    return auth.accessToken;
  };

  const scheduleRefreshToken = () => {
    stopRefreshScheduler();

    if (!auth?.expiresAt) {
      return;
    }

    const timeout = auth.expiresAt - Date.now() - resolvedOptions.timeReserveMs;

    refreshTimeout = setTimeout(
      () => {
        void refreshToken().catch(() => undefined);
      },
      Math.max(timeout, 0),
    );
  };

  function stopRefreshScheduler() {
    if (refreshTimeout) {
      clearTimeout(refreshTimeout);
      refreshTimeout = null;
    }
  }

  const getComparablePathname = (pathname = window.location.pathname) => {
    if (!resolvedOptions.basename) {
      return pathname;
    }

    if (pathname === resolvedOptions.basename) {
      return "/";
    }

    if (pathname.startsWith(`${resolvedOptions.basename}/`)) {
      return pathname.slice(resolvedOptions.basename.length);
    }

    return pathname;
  };

  const client: UdzsAuthClient = {
    getAuth: () => auth,
    getAccessToken: () => auth?.accessToken ?? null,
    hasStoredAuth: () => auth !== null,
    isLoginRedirect: (pathname) =>
      getComparablePathname(pathname) === resolvedOptions.redirectPath,
    isLogoutRedirect: (pathname) =>
      getComparablePathname(pathname) === resolvedOptions.logoutRedirectPath,
    isOAuthFlowPath: (pathname) =>
      client.isLoginRedirect(pathname) || client.isLogoutRedirect(pathname),
    getLoginUrl: async () => {
      localStorage.removeItem(oauthRecoveryKey);

      return createLoginUrl();
    },
    getRecoveryLoginUrl: async () => {
      if (recoveryLoginUrlPromise !== null) {
        return recoveryLoginUrlPromise;
      }

      recoveryLoginUrlPromise = (async () => {
        if (localStorage.getItem(oauthRecoveryKey)) {
          throw new AuthLoginRecoveryFailedError();
        }

        localStorage.setItem(oauthRecoveryKey, "true");

        return createLoginUrl();
      })();

      void recoveryLoginUrlPromise.then(
        () => undefined,
        () => {
          recoveryLoginUrlPromise = null;
        },
      );

      return recoveryLoginUrlPromise;
    },
    clearLoginRecovery: () => {
      localStorage.removeItem(oauthRecoveryKey);
    },
    login: async () => {
      window.location.href = await client.getLoginUrl();
    },
    logout: async () => {
      if (!auth) {
        clearLocalAuth();
        return;
      }

      const authorizationServer = await initAs();
      const logoutUrl = new URL(authorizationServer.end_session_endpoint!);

      logoutUrl.searchParams.set("id_token_hint", auth.idToken);
      logoutUrl.searchParams.set("client_id", resolvedOptions.clientId);

      if (resolvedOptions.clientSecret) {
        logoutUrl.searchParams.set(
          "client_secret",
          resolvedOptions.clientSecret,
        );
      }

      logoutUrl.searchParams.set(
        "post_logout_redirect_uri",
        logoutRedirectUri(),
      );
      clearLocalAuth();

      window.location.href = logoutUrl.toString();
    },
    handleLoginRedirect: (currentUrl) => {
      if (loginRedirectPromise === null) {
        const promise = (async () => {
          const authorizationServer = await initAs();
          const oauthClient = initClient();
          const transaction = readOAuthTransaction();

          if (transaction === null) {
            throw new AuthFlowRestartRequiredError();
          }

          const parameters = oauth.validateAuthResponse(
            authorizationServer,
            oauthClient,
            new URL(currentUrl),
            transaction.state,
          );

          if (oauth.isOAuth2Error(parameters)) {
            throw new Error("OAuth redirect response contains an error.");
          }

          const response = await oauth.authorizationCodeGrantRequest(
            authorizationServer,
            oauthClient,
            parameters,
            redirectUri(),
            transaction.codeVerifier,
          );

          const challenges = oauth.parseWwwAuthenticateChallenges(response);
          if (challenges) {
            throw new Error(
              "OAuth authorization response contains a WWW-Authenticate challenge.",
            );
          }

          const result = await oauth.processAuthorizationCodeOpenIDResponse(
            authorizationServer,
            oauthClient,
            response,
          );

          if (oauth.isOAuth2Error(result)) {
            throw new Error("OAuth authorization response contains an error.");
          }

          setAuth({
            accessToken: result.access_token,
            refreshToken: result.refresh_token ?? null,
            idToken: result.id_token,
            expiresAt:
              result.expires_in !== undefined
                ? Date.now() + result.expires_in * 1000
                : null,
          });

          sessionStorage.removeItem(oauthTransactionKey);
          localStorage.removeItem(oauthRecoveryKey);
          scheduleRefreshToken();
        })();

        loginRedirectPromise = promise;
        void promise.then(
          () => {
            if (loginRedirectPromise === promise) {
              loginRedirectPromise = null;
            }
          },
          () => {
            if (loginRedirectPromise === promise) {
              loginRedirectPromise = null;
            }
          },
        );
      }

      return loginRedirectPromise;
    },
    handleLogoutRedirect: () => {
      clearLocalAuth();
    },
    ensureValidAccessToken,
    applyAxiosAuthorization: () => {
      axios.defaults.headers.common.Authorization = auth?.accessToken
        ? `Bearer ${auth.accessToken}`
        : "";
    },
    clearAuthorization,
    clearLocalAuth,
    storeRedirectUrl: (url) => {
      const redirectPath = normalizeRedirectPath(toInternalRedirectPath(url));

      if (
        !client.isOAuthFlowPath(
          new URL(redirectPath, window.location.origin).pathname,
        )
      ) {
        localStorage.setItem(resolvedOptions.redirectUrlKey, redirectPath);
      }
    },
    consumeRedirectUrl: () => {
      const redirectUrl = normalizeRedirectPath(
        localStorage.getItem(resolvedOptions.redirectUrlKey),
      );

      localStorage.removeItem(resolvedOptions.redirectUrlKey);

      return redirectUrl;
    },
    startRefreshScheduler: scheduleRefreshToken,
    stopRefreshScheduler,
    subscribeToken: (listener) => {
      tokenListeners.add(listener);
      listener(auth?.accessToken ?? null);

      return () => {
        tokenListeners.delete(listener);
      };
    },
  };

  return client;
}

function normalizeBasePath(basename?: string) {
  if (!basename || basename === "/") {
    return "";
  }

  return basename.startsWith("/")
    ? basename.replace(/\/$/, "")
    : `/${basename}`.replace(/\/$/, "");
}
